document.addEventListener('alpine:init', () => {
  Alpine.data('ThemeSection_filterable', (section_id) => {
    const PAGE_INDEX_PARAM = 'pageIndex';

    return {
      focusId: '',
      filterData: [],
      isLoadingMore: false,
      isRestoringLoadMore: false,
      infiniteScrollObserver: null,
      init() {
        initTeleport(this.$root);

        Alpine.store('modals').register('filters', 'leftDrawer');

        window.addEventListener('popstate', this.onHistoryChange.bind(this));
        window.addEventListener('resize', this._setupInfiniteScrollObserver.bind(this));

        this._observeResultsMutations();
        this.$nextTick(() => {
          this.restoreLoadMoreState();
          this._setupInfiniteScrollObserver();
        });
      },
      filterFormSubmit(e) {
        this.focusId = e.target.id;
        Alpine.store('modals').close('filters');
        let form = e.target.form;
        if (e.target.tagName.toLowerCase() === 'form') {
          form = e.target;
        }
        const formData = new FormData(form);
        const searchParams = new URLSearchParams(formData);
        searchParams.delete('price_range');
        this.renderPage(searchParams.toString(), e);
      },
      clearAllFilters(e) {
        Alpine.store('modals').close('filters');
        this.renderPage(new URL(e.currentTarget.href).searchParams.toString());
      },
      renderPage(searchParams, event, updateURLHash = true) {
        const normalizedSearchParams = new URLSearchParams(searchParams);
        normalizedSearchParams.delete(PAGE_INDEX_PARAM);
        const normalizedSearchParamsString = normalizedSearchParams.toString();

        const resultsEl = document.getElementById('facets-results');
        if (resultsEl) {
          resultsEl.classList.add('opacity-50');
        }
        const url = `${window.location.pathname}?section_id=${section_id}&${normalizedSearchParamsString}`;
        const filterDataUrl = (element) => element.url === url;
        this.filterData.some(filterDataUrl)
          ? this.renderFromCache(filterDataUrl, event)
          : this.renderFromFetch(url, event);
        if (updateURLHash) this.updateURLHash(normalizedSearchParamsString);
      },
      renderFromFetch(url, event) {
        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const html = responseText;
            this.filterData = [...this.filterData, { html, url }];
            this.renderMarkup(html);
          });
      },
      renderFromCache(filterDataUrl, event) {
        const html = this.filterData.find(filterDataUrl).html;
        this.renderMarkup(html);
      },
      renderMarkup(html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const innerHTML = parsed.getElementById('facets-filterable').innerHTML;
        const filterableEl = document.getElementById('facets-filterable');

        if (innerHTML !== filterableEl.innerHTML) {
          filterableEl.closest('.shopify-section').dispatchEvent(
            new CustomEvent('cascade:section:willmutate', {
              bubbles: true,
            })
          );

          setTimeout(() => {
            filterableEl.querySelectorAll('[x-teleport]').forEach((el) => {
              el.remove();
            });

            filterableEl.innerHTML = innerHTML;

            this.$nextTick(() => {
              initTeleport(filterableEl);
              this.restoreLoadMoreState();
            });
          }, 300);
        } else {
          const existingResultsEl = document.getElementById('facets-results');
          if (existingResultsEl) {
            existingResultsEl.classList.remove('opacity-50');
          }
          this.restoreLoadMoreState();
        }

        const countEl = parsed.getElementById('facets-results-count');
        if (countEl) {
          liveRegion(countEl.innerHTML);
        }

        const element = document.getElementById(this.focusId);

        if (element) {
          setTimeout(() => {
            document.getElementById(this.focusId).focus();
          }, 50);
        }
      },
      updateURLHash(searchParams) {
        history.pushState(
          { searchParams },
          '',
          `${window.location.pathname}${
            searchParams && '?'.concat(searchParams)
          }`
        );
      },
      onHistoryChange(event) {
        const searchParams =
          event.state?.searchParams || window.location.search.slice(1);
        this.renderPage(searchParams, null, false);
      },
      async loadMore(event) {
        if (event) {
          event.preventDefault();
        }

        await this._loadMorePage();
      },
      async _loadMorePage({ persist = true } = {}) {
        if (this.isLoadingMore) {
          return false;
        }

        const loadMoreEl = document.getElementById('facets-load-more');
        const resultsEl = document.getElementById('facets-results');

        if (!loadMoreEl || !resultsEl) {
          return false;
        }

        const nextUrl = loadMoreEl.dataset.nextUrl;
        const buttonEl = loadMoreEl.querySelector('button');

        if (!nextUrl) {
          loadMoreEl.remove();
          this._disconnectInfiniteScrollObserver();
          return false;
        }

        this.isLoadingMore = true;
        if (buttonEl) {
          buttonEl.disabled = true;
          buttonEl.setAttribute('aria-busy', 'true');
        }

        try {
          const fetchUrl = this._buildSectionRenderUrl(nextUrl);
          const response = await fetch(fetchUrl);
          const responseText = await response.text();
          const parsed = new DOMParser().parseFromString(responseText, 'text/html');

          const nextResultsEl = parsed.getElementById('facets-results');
          if (!nextResultsEl) {
            return false;
          }

          resultsEl.insertAdjacentHTML('beforeend', nextResultsEl.innerHTML);

          const nextLoadMoreEl = parsed.getElementById('facets-load-more');
          const nextPage = parseInt(nextLoadMoreEl?.dataset.currentPage || '1', 10);
          const nextPageUrl = nextLoadMoreEl?.dataset.nextUrl || '';

          loadMoreEl.dataset.currentPage = String(nextPage);
          loadMoreEl.dataset.totalPages =
            nextLoadMoreEl?.dataset.totalPages || loadMoreEl.dataset.totalPages || '';
          loadMoreEl.dataset.nextUrl = nextPageUrl;

          if (!nextPageUrl) {
            loadMoreEl.remove();
            this._disconnectInfiniteScrollObserver();
          } else if (buttonEl) {
            buttonEl.disabled = false;
          }

          if (persist) {
            this._persistLoadMorePage(nextPage);
            this._setPageIndexInURL(nextPage, { push: true });
          }

          return true;
        } catch {
          if (buttonEl) {
            buttonEl.disabled = false;
          }
          return false;
        } finally {
          this.isLoadingMore = false;
          if (buttonEl) {
            buttonEl.removeAttribute('aria-busy');
          }
          this._setupInfiniteScrollObserver();
        }
      },
      async restoreLoadMoreState() {
        if (this.isRestoringLoadMore) {
          return;
        }

        const loadMoreEl = document.getElementById('facets-load-more');
        if (!loadMoreEl) {
          this._persistLoadMorePage(1);
          this._setPageIndexInURL(1, { push: false });
          this._disconnectInfiniteScrollObserver();
          return;
        }

        const currentPage = parseInt(loadMoreEl.dataset.currentPage || '1', 10);
        const targetPage = this._getRequestedLoadMorePage() || this._getStoredLoadMorePage();

        if (targetPage <= currentPage) {
          this._persistLoadMorePage(currentPage);
          this._setPageIndexInURL(currentPage, { push: false });
          return;
        }

        this.isRestoringLoadMore = true;

        let restoredPage = currentPage;

        try {
          while (restoredPage < targetPage) {
            const loaded = await this._loadMorePage({ persist: false });
            if (!loaded) {
              break;
            }

            const updatedLoadMoreEl = document.getElementById('facets-load-more');
            if (!updatedLoadMoreEl) {
              break;
            }

            restoredPage = parseInt(updatedLoadMoreEl.dataset.currentPage || '1', 10);
          }
        } finally {
          this.isRestoringLoadMore = false;
          this._persistLoadMorePage(restoredPage);
          this._setPageIndexInURL(restoredPage, { push: false });
          this._setupInfiniteScrollObserver();
        }
      },
      _disconnectInfiniteScrollObserver() {
        if (this.infiniteScrollObserver) {
          this.infiniteScrollObserver.disconnect();
          this.infiniteScrollObserver = null;
        }
      },
      _setupInfiniteScrollObserver() {
        this._disconnectInfiniteScrollObserver();

        if (this.isRestoringLoadMore) {
          return;
        }

        const loadMoreEl = document.getElementById('facets-load-more');
        if (!loadMoreEl) {
          return;
        }

        const sentinelEl =
          loadMoreEl.querySelector('[data-load-more-sentinel]') || loadMoreEl;

        this.infiniteScrollObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry?.isIntersecting || this.isLoadingMore || this.isRestoringLoadMore) {
              return;
            }

            this._loadMorePage();
          },
          { rootMargin: '250px 0px' }
        );

        this.infiniteScrollObserver.observe(sentinelEl);
      },
      _getRequestedLoadMorePage() {
        const url = new URL(window.location.href);
        const requestedPage = parseInt(url.searchParams.get(PAGE_INDEX_PARAM) || '0', 10);
        return Number.isFinite(requestedPage) && requestedPage > 1 ? requestedPage : null;
      },
      _setPageIndexInURL(page, { push = false } = {}) {
        const sanitizedPage = Number.isFinite(page) && page > 1 ? page : 1;
        const url = new URL(window.location.href);
        const currentPage = parseInt(url.searchParams.get(PAGE_INDEX_PARAM) || '1', 10);

        if (currentPage === sanitizedPage || (!url.searchParams.has(PAGE_INDEX_PARAM) && sanitizedPage === 1)) {
          return;
        }

        if (sanitizedPage <= 1) {
          url.searchParams.delete(PAGE_INDEX_PARAM);
        } else {
          url.searchParams.set(PAGE_INDEX_PARAM, String(sanitizedPage));
        }

        const nextSearchParams = url.searchParams.toString();
        const nextRelativeUrl = `${url.pathname}${nextSearchParams ? `?${nextSearchParams}` : ''}${url.hash}`;

        history[push ? 'pushState' : 'replaceState'](
          { searchParams: nextSearchParams },
          '',
          nextRelativeUrl
        );
      },
      _buildSectionRenderUrl(url) {
        const renderUrl = new URL(url, window.location.origin);
        renderUrl.searchParams.set('section_id', section_id);
        return renderUrl.toString();
      },
      _getLoadMoreStorageKey(searchParams = window.location.search.slice(1)) {
        const normalizedSearchParams = new URLSearchParams(searchParams);
        normalizedSearchParams.delete('page');
        normalizedSearchParams.delete(PAGE_INDEX_PARAM);
        normalizedSearchParams.delete('section_id');
        const paramsString = normalizedSearchParams.toString();
        return `collection-load-more:${window.location.pathname}?${paramsString}`;
      },
      _persistLoadMorePage(page, searchParams = window.location.search.slice(1)) {
        try {
          sessionStorage.setItem(this._getLoadMoreStorageKey(searchParams), String(page));
        } catch {
          // Ignore session storage errors in private browsing modes.
        }
      },
      _getStoredLoadMorePage(searchParams = window.location.search.slice(1)) {
        try {
          const savedPage = parseInt(
            sessionStorage.getItem(this._getLoadMoreStorageKey(searchParams)) || '1',
            10
          );
          return Number.isFinite(savedPage) && savedPage > 0 ? savedPage : 1;
        } catch {
          return 1;
        }
      },
      _observeResultsMutations() {
        const containerEl = document.getElementById('facets-filterable');

        const mutationObserverOptions = {
          childList: true,
          attributes: false,
          // Omit (or set to false) to observe only changes to the parent node
          subtree: false,
        };

        const callback = (mutationList, observer) => {
          mutationList.forEach((mutation) => {
            if (mutation.type === 'childList') {
              mutation.target.closest('.shopify-section').dispatchEvent(
                new CustomEvent('cascade:section:hasmutated', {
                  bubbles: true,
                })
              );
            }
          });
        };

        const mutationObserver = new MutationObserver(callback);
        mutationObserver.observe(containerEl, mutationObserverOptions);
      },
    };
  });
});
